import { motion, AnimatePresence } from "framer-motion";
import { Users } from "lucide-react";
import { useLiveCount } from "@/hooks/useLiveCount";

type Props = {
  /** Compact pill (default) or larger card */
  variant?: "pill" | "inline";
  /** Override label */
  label?: string;
};

/**
 * Real live online user count from Supabase presence.
 * No fake numbers — backed by `presence_heartbeat` and realtime updates.
 */
const LiveCounter = ({ variant = "pill", label = "online now" }: Props) => {
  const { count, loading } = useLiveCount();
  // Show plain integer (no thousand-separators / no "k" abbreviations) — only real backend value.
  const display = loading ? "…" : String(count);

  if (variant === "inline") {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-destructive/10 border border-destructive/30 backdrop-blur-sm">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive" />
        </span>
        <span className="text-[11px] font-semibold">
          🔥 <AnimatedNumber value={display} /> <span className="text-muted-foreground font-normal">{label}</span>
        </span>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/30 border border-border/30 backdrop-blur-sm">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
      </span>
      <Users className="w-3 h-3 text-muted-foreground" />
      <span className="text-[11px] font-semibold text-foreground">
        <AnimatedNumber value={display} />
        <span className="text-muted-foreground font-normal"> {label}</span>
      </span>
    </div>
  );
};

const AnimatedNumber = ({ value }: { value: string }) => (
  <AnimatePresence mode="popLayout">
    <motion.span
      key={value}
      initial={{ y: 6, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -6, opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="text-primary font-bold inline-block"
    >
      {value}
    </motion.span>
  </AnimatePresence>
);

export default LiveCounter;
